import { trocarSenhaSchema } from '@projetive-op/domain'
import { useMutation } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { z } from 'zod'

import { LogoOP } from '@/components/marca'
import { Button } from '@/components/ui/button'
import { Campo, Input, MensagemFormulario } from '@/components/ui/campo'
import { toast } from '@/components/ui/toast'
import { mensagemDeErro } from '@/lib/erros'
import { type ErrosFormulario, focarPrimeiroErro, validar } from '@/lib/formulario'
import { PERFIS_WEB, exigirSessao } from '@/lib/sessao'
import { useTRPC } from '@/lib/trpc'

/**
 * Troca da própria senha (gestão e diretoria). Quem recebeu a senha
 * provisória troca por uma sua no primeiro acesso. O operador usa PIN, que a
 * gestão define em Usuários.
 */
export const Route = createFileRoute('/minha-senha')({
  validateSearch: z.object({ voltar: z.enum(['/gestao', '/diretoria']).optional() }),
  beforeLoad: async ({ context, location }) => {
    await exigirSessao({ context, location, perfis: PERFIS_WEB })
  },
  component: MinhaSenha,
})

function MinhaSenha() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const { voltar = '/gestao' } = Route.useSearch()
  const [erros, setErros] = useState<ErrosFormulario>({})

  const trocar = useMutation(
    trpc.auth.trocarSenha.mutationOptions({
      onSuccess: async () => {
        toast.sucesso('Senha trocada. Use a nova no próximo acesso.')
        await navigate({ to: voltar })
      },
      onError: (e) => {
        const msg = mensagemDeErro(e)
        setErros(msg === 'Senha atual incorreta' ? { senhaAtual: msg } : { _: msg })
      },
    }),
  )

  const enviar = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    const form = ev.currentTarget
    const r = validar(trocarSenhaSchema, Object.fromEntries(new FormData(form)))
    if (r.erros) {
      setErros(r.erros)
      focarPrimeiroErro(form)
      return
    }
    setErros({})
    trocar.mutate(r.dados)
  }

  return (
    <main className="grid min-h-full place-items-center p-4">
      <form onSubmit={enviar} className="flex w-full max-w-sm flex-col gap-5 card p-8" noValidate>
        <div className="flex flex-col gap-4">
          <Link
            to={voltar}
            className="inline-flex items-center gap-1 self-start text-sm text-fg-muted hover:text-fg"
          >
            <ArrowLeft className="size-4" aria-hidden /> Voltar
          </Link>
          <LogoOP tamanho="size-10" />
          <div>
            <h1 className="text-xl font-bold">Trocar minha senha</h1>
            <p className="text-sm text-fg-muted">
              Recebeu uma senha provisória? Troque por uma sua aqui.
            </p>
          </div>
        </div>
        <Campo label="Senha atual" erro={erros.senhaAtual}>
          {(p) => (
            <Input
              {...p}
              name="senhaAtual"
              type="password"
              autoComplete="current-password"
              autoFocus
              required
            />
          )}
        </Campo>
        <Campo label="Nova senha" erro={erros.novaSenha} dica="Ao menos 8 caracteres.">
          {(p) => (
            <Input {...p} name="novaSenha" type="password" autoComplete="new-password" required />
          )}
        </Campo>
        <Campo label="Repita a nova senha" erro={erros.confirmacao}>
          {(p) => (
            <Input {...p} name="confirmacao" type="password" autoComplete="new-password" required />
          )}
        </Campo>
        <MensagemFormulario erro={erros._} />
        <Button type="submit" carregando={trocar.isPending} className="mt-1">
          Trocar senha
        </Button>
      </form>
    </main>
  )
}
